import { DestroyRef, Injectable, inject } from '@angular/core';
import { ConfirmationService } from 'primeng/api';

/**
 * «Cambios sin guardar»: una pantalla con un formulario largo registra una función que dice si tiene trabajo pendiente y se da de baja al
 * destruirse. Antes de cambiar de obra (que vuelve a cargar la pantalla y lo perdería) el portal pregunta una sola vez, para todas.
 *
 * Uso en una pantalla (en el constructor o un campo):  `inject(CambiosPendientesService).vigilar(() => this.dirty())`
 * `vigilar` se desregistra solo al destruirse el componente (usa el DestroyRef del contexto de inyección).
 */
@Injectable({ providedIn: 'root' })
export class CambiosPendientesService {
  /** Opcional: en las pruebas sin el diálogo de confirmación no hay a quién preguntar (se deja pasar). */
  private confirmacion = inject(ConfirmationService, { optional: true });
  private fuentes = new Set<() => boolean>();
  private descarteAprobado = false;

  /**
   * ¿La persona ya aceptó perder lo pendiente en el cambio de obra que está en curso? Las pantallas con su propia confirmación al salir de la ruta
   * (el detalle de Producción) la omiten mientras tanto: sin esto, después de «Cambiar de obra» preguntaban otra vez lo mismo.
   */
  get descarteYaConfirmado(): boolean { return this.descarteAprobado; }

  /** El cambio de obra terminó (o se cancelo): vuelve a valer cualquier otra confirmación de salida. */
  terminarDescarte(): void { this.descarteAprobado = false; }

  /** Registra una fuente de cambios pendientes; devuelve la función que la da de baja. Dentro de un contexto de inyección se da de baja sola. */
  vigilar(hayCambios: () => boolean, destroyRef?: DestroyRef): () => void {
    this.fuentes.add(hayCambios);
    const baja = () => { this.fuentes.delete(hayCambios); };
    (destroyRef ?? this.destroyRefDelContexto())?.onDestroy(baja);
    return baja;
  }

  /** El DestroyRef del componente que llama (si se llama desde su constructor o un campo); fuera de un contexto de inyección, ninguno. */
  private destroyRefDelContexto(): DestroyRef | undefined {
    try { return inject(DestroyRef); } catch { return undefined; }
  }

  /** ¿Alguna pantalla abierta tiene trabajo sin guardar? Una fuente que falla no cuenta (no puede bloquear el cambio). */
  hay(): boolean {
    for (const f of this.fuentes) {
      try { if (f()) return true; } catch { /* se ignora */ }
    }
    return false;
  }

  /**
   * Antes de una acción que perdería lo pendiente (cambiar de obra). Sin cambios pendientes responde `true` al instante; con ellos muestra la
   * confirmación y responde lo que decida la persona.
   */
  confirmarCambioDeObra(): Promise<boolean> {
    if (!this.hay() || !this.confirmacion) return Promise.resolve(true);
    return new Promise<boolean>(resolver => {
      this.confirmacion!.confirm({
        header: 'Cambios sin guardar',
        message: 'Tienes cambios sin guardar. Si cambias de obra se perderán.',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Cambiar de obra',
        rejectLabel: 'Seguir aquí',
        acceptButtonProps: { severity: 'danger' },
        rejectButtonProps: { severity: 'secondary', outlined: true },
        defaultFocus: 'reject',
        accept: () => { this.descarteAprobado = true; resolver(true); },
        reject: () => resolver(false),
      });
    });
  }
}
